import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { apiGet, apiPath, apiPost } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

import type { LcrNsfrDetail, TriggerLcrRequest } from './types';

const lcrKey = (date: string | undefined) =>
  ['alm', 'liquidity', 'latest', date] as const;

/**
 * GET /api/v1/alm/liquidity?date=YYYY-MM-DD
 *
 * Returns the persisted LCR/NSFR result for the given tenant + date,
 * or null if no calculation has run yet. When date is undefined the
 * server returns the most recent result.
 */
export function useLatestLcr(
  date: string | undefined,
  options?: Omit<
    UseQueryOptions<LcrNsfrDetail | null, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: lcrKey(date),
    enabled: Boolean(date),
    queryFn: async () => {
      const url = date
        ? `${apiPath('alm', 'liquidity')}?date=${encodeURIComponent(date)}`
        : apiPath('alm', 'liquidity');
      const data = await apiGet<LcrNsfrDetail | null>(url);
      return data ?? null;
    },
    ...options,
  });
}

/**
 * POST /api/v1/alm/liquidity/calculate — triggers an on-demand LCR/NSFR calculation.
 * Server returns the computed result directly (synchronous, no Kafka hop).
 */
export function useTriggerLcr(
  options?: UseMutationOptions<LcrNsfrDetail, CubixApiError, TriggerLcrRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiPost<LcrNsfrDetail, TriggerLcrRequest>(
        apiPath('alm', 'liquidity', 'calculate'),
        body,
      ),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['alm', 'liquidity'] });
      return options?.onSuccess?.(...args);
    },
  });
}
